import React, { FC, useState, useMemo } from 'react';
import { styled } from 'linaria/lib/react';

import { useGetUserPickedTopics } from 'graphQL/cards/userCards/hooks';
import { COLORS, FONTS } from 'styles/constants';
import useTheme from 'helpers/useTheme';
import getContrastingTextColor from 'helpers/getContrastingTextColor';
import noiseTexture from 'assets/images/noise.png';

interface IPicksFilterProps {
  onFiltersChange: (filters: { topicIds?: number[]; mediaTagNames?: string[] }) => void;
  backgroundColor?: string;
  cardsSource?: Array<{
    topicTags?: { items?: Array<{ id: number; name: string }> };
    cardMediaTags?: { items?: Array<{ id: number; name: string }> };
  }>;
}

const StyledFilterContainer = styled.div<{ isDark: boolean; fontColor: string; backgroundColor?: string }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;
  align-items: center;
  color: ${({ fontColor }) => fontColor};
  background: url(${noiseTexture}),
    ${({ backgroundColor, isDark }) => backgroundColor ?? (isDark ? COLORS.black[100] : COLORS.white[100])};
  background-repeat: repeat;
  background-size: auto;
`;

const StyledFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 378px;
`;

const StyledScrollContainer = styled.div<{ isDark: boolean; isTopics?: boolean }>`
  display: flex;
  width: 378px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(115, 115, 115, 0) 0%,
    #d9d9d9 8%,
    rgba(115, 115, 115, 0.4) 21.99%,
    #d9d9d9 38.12%,
    #d9d9d9 60.44%,
    rgba(115, 115, 115, 0.4) 75.19%,
    #d9d9d9 90.43%,
    rgba(115, 115, 115, 0) 100%
  );
  mask-image: linear-gradient(
    90deg,
    rgba(115, 115, 115, 0) 0%,
    #d9d9d9 8%,
    rgba(115, 115, 115, 0.4) 21.99%,
    #d9d9d9 38.12%,
    #d9d9d9 60.44%,
    rgba(115, 115, 115, 0.4) 75.19%,
    #d9d9d9 90.43%,
    rgba(115, 115, 115, 0) 100%
  );
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledFilterChips = styled.div<{ isTopics?: boolean }>`
  display: flex;
  gap: 8px;
  padding: 0 16px;
  min-width: max-content;
`;

const StyledFilterChip = styled.button<{
  isSelected: boolean;
  isMediaType?: boolean;
  fontColor: string;
  isTopics?: boolean;
}>`
  display: flex;
  padding: 10px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 22px;
  backdrop-filter: blur(2px);
  background: ${({ isSelected, fontColor }) => (isSelected ? `${fontColor}1A` : 'transparent')};
  color: ${({ isMediaType, fontColor }) => {
    if (isMediaType) {
      return fontColor;
    }
    // Non-media types get a slightly muted version
    return fontColor === COLORS.white[100] ? COLORS.white[60] : COLORS.brown.dark[70];
  }};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  white-space: nowrap;
  position: relative;
  z-index: ${({ isSelected }) => (isSelected ? 2 : 0)};
`;

const PicksFilter: FC<IPicksFilterProps> = ({ onFiltersChange, backgroundColor, cardsSource }) => {
  const isDark = useTheme();
  const { topics, mediaTags } = useGetUserPickedTopics(undefined, cardsSource);

  // Derive font color from background color for readability
  const effectiveBackgroundColor = backgroundColor ?? (isDark ? COLORS.black[100] : COLORS.white[100]);
  const computedFontColor = getContrastingTextColor(effectiveBackgroundColor);

  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [selectedMediaTagNames, setSelectedMediaTagNames] = useState<string[]>([]);

  // Helper function to capitalize media tag names
  const capitalizeMediaTagName = (name: string): string => {
    // Special case for "Tv" to display as "TV" within a string
    if (name.toLowerCase().includes('tv')) {
      return name.replace(/tv/gi, 'TV');
    }
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Memoize filter options to avoid unnecessary re-renders
  const allFilterOptions = useMemo(() => {
    const mediaTypeOptions = mediaTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      displayName: capitalizeMediaTagName(tag.name),
      type: 'media' as const,
    }));

    // Get all media tag names to exclude from topics (case-insensitive, trimmed)
    const mediaTagNames = new Set(mediaTags.map((tag) => tag.name.toLowerCase().trim()));

    // Filter out topics that are also media tags (case-insensitive, trimmed comparison)
    const topicOptions = topics
      .filter((topic) => !mediaTagNames.has(topic.name.toLowerCase().trim()))
      .map((topic) => ({
        id: topic.id,
        name: topic.name,
        type: 'topic' as const,
      }));

    return { mediaTypeOptions, topicOptions };
  }, [mediaTags, topics]);

  const showMedia = allFilterOptions.mediaTypeOptions.length >= 2;
  const showTopics = allFilterOptions.topicOptions.length >= 2;

  if (!showMedia && !showTopics) return null;

  const handleTopicToggle = (topicId: number) => {
    const newSelectedTopicIds = selectedTopicIds.includes(topicId)
      ? selectedTopicIds.filter((id) => id !== topicId)
      : [...selectedTopicIds, topicId];

    setSelectedTopicIds(newSelectedTopicIds);
    onFiltersChange({
      topicIds: newSelectedTopicIds.length > 0 ? newSelectedTopicIds : undefined,
      mediaTagNames: selectedMediaTagNames.length > 0 ? selectedMediaTagNames : undefined,
    });
  };

  const handleMediaTagToggle = (mediaTagName: string) => {
    const newSelectedMediaTagNames = selectedMediaTagNames.includes(mediaTagName)
      ? selectedMediaTagNames.filter((name) => name !== mediaTagName)
      : [...selectedMediaTagNames, mediaTagName];

    setSelectedMediaTagNames(newSelectedMediaTagNames);
    onFiltersChange({
      topicIds: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
      mediaTagNames: newSelectedMediaTagNames.length > 0 ? newSelectedMediaTagNames : undefined,
    });
  };

  return (
    <StyledFilterContainer isDark={isDark} fontColor={computedFontColor} backgroundColor={backgroundColor}>
      {/* Media Types Row - Top row with all media types */}
      {showMedia && (
        <StyledFilterSection>
          <StyledScrollContainer isDark={isDark}>
            <StyledFilterChips>
              {allFilterOptions.mediaTypeOptions.map((mediaTag) => (
                <StyledFilterChip
                  key={`media-${mediaTag.id}`}
                  isSelected={selectedMediaTagNames.includes(mediaTag.name)}
                  isMediaType
                  fontColor={computedFontColor}
                  onClick={() => handleMediaTagToggle(mediaTag.name)}
                >
                  {mediaTag.displayName}
                </StyledFilterChip>
              ))}
            </StyledFilterChips>
          </StyledScrollContainer>
        </StyledFilterSection>
      )}

      {/* Topics Row - Bottom row with maximum 8 topics */}
      {showTopics && (
        <StyledFilterSection>
          <StyledScrollContainer isDark={isDark} isTopics>
            <StyledFilterChips isTopics>
              {allFilterOptions.topicOptions.slice(0, 8).map((topic) => (
                <StyledFilterChip
                  key={`topic-${topic.id}`}
                  isSelected={selectedTopicIds.includes(topic.id)}
                  fontColor={computedFontColor}
                  onClick={() => handleTopicToggle(topic.id)}
                >
                  {topic.name}
                </StyledFilterChip>
              ))}
            </StyledFilterChips>
          </StyledScrollContainer>
        </StyledFilterSection>
      )}
    </StyledFilterContainer>
  );
};

export default PicksFilter;
